import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import MainLayout from "./MainLayout";
import Swal from "sweetalert2";
import { Power, Download, Loader2 } from "lucide-react";
import Apiurl from "../../Helpers/apiUrl";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserOpt } from "../../redux/slices/optionSlice";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const FY_MONTH_ORDER = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];

const getCurrentFYMonths = () => {
    const currentMonth = new Date().getMonth() + 1;
    const months = [];
    if (currentMonth >= 4) {
        for (let m = 4; m <= currentMonth; m++) {
            months.push(m.toString());
        }
    } else {
        for (let m = 4; m <= 12; m++) {
            months.push(m.toString());
        }
        for (let m = 1; m <= currentMonth; m++) {
            months.push(m.toString());
        }
    }
    return months;
};

function FilterPill({ name, id, list = [], icon, selected, onToggle, activeDropdown, setActiveDropdown }) {
    const safeSelected = Array.isArray(selected) ? selected : (selected !== undefined && selected !== null ? [selected] : []);
    const hasSelections = safeSelected.length > 0;
    const isOpen = activeDropdown === id;
    const [searchQuery, setSearchQuery] = useState('');
    const closeTimeoutRef = useRef(null);

    const selectedSet = useMemo(() => new Set(safeSelected.map(String)), [safeSelected]);

    useEffect(() => {
        if (!isOpen) setSearchQuery('');
    }, [isOpen]);

    const handleMouseEnter = useCallback(() => {
        if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
        setActiveDropdown(id);
    }, [id, setActiveDropdown]);

    const handleMouseLeave = useCallback(() => {
        closeTimeoutRef.current = setTimeout(() => setActiveDropdown(null), 180);
    }, [id, setActiveDropdown]);

    const filteredList = useMemo(() => {
        const query = searchQuery.toLowerCase().trim();
        const filtered = list.filter((item) => {
            if (!item) return false;
            if (!query) return true;
            return String(item.label).toLowerCase().includes(query);
        });
        return filtered.sort((a, b) => {
            const checkedA = selectedSet.has(String(a.val));
            const checkedB = selectedSet.has(String(b.val));
            if (checkedA && !checkedB) return -1;
            if (!checkedA && checkedB) return 1;
            return String(a.label).localeCompare(String(b.label), undefined, { sensitivity: 'base', numeric: true });
        });
    }, [list, searchQuery, selectedSet]);

    const isAllFilteredSelected = useMemo(() => {
        if (filteredList.length === 0) return false;
        return filteredList.every((item) => selectedSet.has(String(item.val)));
    }, [filteredList, selectedSet]);

    const handleSelectAllToggle = () => {
        filteredList.forEach((item) => {
            const valStr = String(item.val);
            const isChecked = selectedSet.has(valStr);
            if (isAllFilteredSelected && isChecked) onToggle(id, item.val);
            else if (!isAllFilteredSelected && !isChecked) onToggle(id, item.val);
        });
    };

    return (
        <div className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <button
                type="button"
                onClick={() => setActiveDropdown(isOpen ? null : id)}
                className={`flex items-center gap-1.5 h-8 px-3 rounded-xl text-[11px] font-bold border transition-all duration-150 ${hasSelections ? 'bg-[#0a2540] border-[#0a2540] text-white shadow-md' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'}`}
            >
                <span className={hasSelections ? 'text-white/70' : 'text-slate-400'}>{React.cloneElement(icon, { sx: { fontSize: 13 } })}</span>
                <span>{name}</span>
                {hasSelections ? (
                    <span className="bg-white/20 text-white text-[10px] font-extrabold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">{safeSelected.length}</span>
                ) : (
                    <KeyboardArrowDownIcon sx={{ fontSize: 13, opacity: 0.5, transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }} />
                )}
            </button>
            {isOpen && (
                <>
                    <div className="absolute top-full left-0 h-1.5 w-full" />
                    <div className="absolute top-full left-0 mt-1.5 w-60 bg-white border border-slate-200 rounded-2xl shadow-xl p-1.5 flex flex-col gap-0.5 z-50 animate-[fadeIn_0.12s_ease-out]">
                        <div className="relative flex items-center mb-1 px-1">
                            <SearchIcon className="absolute left-2.5 text-slate-400" sx={{ fontSize: 13 }} />
                            <input type="text" placeholder={`Search ${name}...`} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full h-7 pl-7 pr-2.5 bg-slate-50 border border-slate-100 rounded-lg text-[11px] text-slate-800 placeholder-slate-400 focus:outline-none focus:border-slate-200 focus:bg-white transition-all" />
                        </div>
                        {filteredList.length > 0 && (
                            <div onClick={handleSelectAllToggle} className="flex items-center justify-between px-2.5 py-1.5 mb-1 rounded-lg text-[10px] font-bold bg-slate-100/70 text-slate-700 cursor-pointer hover:bg-slate-200/70 transition-colors">
                                <div className="flex items-center gap-1.5">
                                    {isAllFilteredSelected ? <CheckBoxIcon sx={{ fontSize: 14, color: '#0a2540' }} /> : <CheckBoxOutlineBlankIcon sx={{ fontSize: 14, color: '#cbd5e1' }} />}
                                    <span>{isAllFilteredSelected ? 'Deselect All' : 'Select All'}</span>
                                </div>
                                <span className="text-[9px] text-slate-400 font-normal">({filteredList.length})</span>
                            </div>
                        )}
                        <div className="max-h-48 overflow-y-auto flex flex-col gap-0.5 custom-scrollbar">
                            {filteredList.length > 0 ? (
                                filteredList.slice(0, 100).map((item) => {
                                    const checked = selectedSet.has(String(item.val));
                                    return (
                                        <div key={item.val} onClick={() => onToggle(id, item.val)} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-xl text-[11px] font-semibold cursor-pointer transition-colors ${checked ? 'bg-slate-50 text-slate-900' : 'text-slate-500 hover:bg-slate-50'}`}>
                                            {checked ? <CheckBoxIcon sx={{ fontSize: 14, color: '#0a2540' }} /> : <CheckBoxOutlineBlankIcon sx={{ fontSize: 14, color: '#cbd5e1' }} />}
                                            <span className={`truncate ${checked ? 'font-bold text-slate-900' : ''}`}>{item.label}</span>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="text-[10px] text-slate-400 text-center py-3 font-medium">No results found</div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

function MonthMultiSelectPicker({ selectedMonths = [], onToggle, activeDropdown, setActiveDropdown }) {
    const dropdownId = 'months';
    const isOpen = activeDropdown === dropdownId;
    const closeTimeoutRef = useRef(null);

    const rangeLabel = useMemo(() => {
        if (selectedMonths.length === 12) return 'Full FY';
        if (selectedMonths.length === 0) return 'Select Months';
        if (selectedMonths.length === 1) return MONTHS[selectedMonths[0] - 1]?.slice(0, 3);
        const sorted = [...selectedMonths].sort((a, b) => FY_MONTH_ORDER.indexOf(Number(a)) - FY_MONTH_ORDER.indexOf(Number(b)));
        return `${MONTHS[sorted[0] - 1]?.slice(0, 3)} (+${selectedMonths.length - 1})`;
    }, [selectedMonths]);

    return (
        <div className="relative" onMouseEnter={() => { if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current); setActiveDropdown(dropdownId); }} onMouseLeave={() => { closeTimeoutRef.current = setTimeout(() => setActiveDropdown(null), 180); }}>
            <button type="button" onClick={() => setActiveDropdown(isOpen ? null : dropdownId)} className={`flex items-center gap-1.5 h-8 px-3 rounded-xl text-[11px] font-bold border transition-all duration-150 ${selectedMonths.length > 0 ? 'bg-[#0a2540] border-[#0a2540] text-white shadow-md' : 'bg-white border-slate-200 text-slate-600'}`}>
                <CalendarMonthIcon sx={{ fontSize: 13 }} />
                <span>{rangeLabel}</span>
                <KeyboardArrowDownIcon sx={{ fontSize: 13, transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
            </button>
            {isOpen && (
                <div className="absolute top-full right-0 mt-1.5 w-48 bg-white border border-slate-200 rounded-2xl shadow-xl p-1.5 z-50 flex flex-col gap-0.5">
                    <div className="text-[9px] uppercase tracking-wider font-extrabold text-slate-400 px-2 py-1">Months (FY Order)</div>
                    <div onClick={() => {
                        const isAllSelected = selectedMonths.length === FY_MONTH_ORDER.length;
                        FY_MONTH_ORDER.forEach((mNum) => {
                            const isChecked = selectedMonths.includes(mNum.toString());
                            if (isAllSelected && isChecked) onToggle('months', mNum.toString());
                            else if (!isAllSelected && !isChecked) onToggle('months', mNum.toString());
                        });
                    }} className="flex items-center justify-between px-2.5 py-1.5 mb-1 rounded-lg text-[10px] font-bold bg-slate-100/70 text-slate-700 cursor-pointer hover:bg-slate-200/70 transition-colors">
                        <div className="flex items-center gap-1.5">
                            {selectedMonths.length === FY_MONTH_ORDER.length ? <CheckBoxIcon sx={{ fontSize: 14, color: "#0a2540" }} /> : <CheckBoxOutlineBlankIcon sx={{ fontSize: 14, color: "#cbd5e1" }} />}
                            <span>{selectedMonths.length === FY_MONTH_ORDER.length ? "Deselect All" : "Select All"}</span>
                        </div>
                        <span className="text-[9px] text-slate-400 font-normal">({FY_MONTH_ORDER.length})</span>
                    </div>
                    <div className="max-h-56 overflow-y-auto custom-scrollbar flex flex-col gap-0.5">
                        {FY_MONTH_ORDER.map((mNum) => {
                            const checked = selectedMonths.includes(mNum.toString());
                            return (
                                <div key={mNum} onClick={() => onToggle('months', mNum.toString())} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-xl text-[11px] font-semibold cursor-pointer transition-colors ${checked ? 'bg-slate-50 text-slate-900' : 'text-slate-500 hover:bg-slate-50'}`}>
                                    {checked ? <CheckBoxIcon sx={{ fontSize: 14, color: '#0a2540' }} /> : <CheckBoxOutlineBlankIcon sx={{ fontSize: 14, color: '#cbd5e1' }} />}
                                    <span className={checked ? 'font-bold' : ''}>{MONTHS[mNum - 1]}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

const AdminAiManagement = () => {
  const dispatch = useDispatch();
  const { userOpt } = useSelector((s) => s.options);

  const [askAiEnabled, setAskAiEnabled] = useState(false);
  const [loadingToggle, setLoadingToggle] = useState(false);
  
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  
  const [totalTokens, setTotalTokens] = useState(0);

  // Filters setup: FY logic
  const currentYear = new Date().getFullYear();
  const fyStart = new Date().getMonth() < 3 ? currentYear - 1 : currentYear;
  
  // Format to FY years "2026-2027"
  const yearOptions = [fyStart, fyStart - 1, fyStart - 2].map(y => ({
      label: `${y}-${y + 1}`,
      val: y.toString()
  }));

  const [filters, setFilters] = useState({
      years: [fyStart.toString()],
      months: getCurrentFYMonths(),
      users: []
  });

  const [activeDropdown, setActiveDropdown] = useState(null);
  
  const [perPage, setPerPage] = useState(10);
  const [exporting, setExporting] = useState(false);

  // Read More state
  const [expandedRows, setExpandedRows] = useState({});

  const token = localStorage.getItem("u_token");

  useEffect(() => {
    dispatch(fetchUserOpt());
    fetchSettings();
  }, []);

  useEffect(() => {
    fetchLogs(1);
  }, [filters.years, filters.months, filters.users, perPage]);

  const fetchSettings = async () => {
    try {
      const settingsRes = await fetch(`${Apiurl}system-settings`, { cache: 'no-store' });
      const data = await settingsRes.json();
      if (data.status) {
        setAskAiEnabled(data.ask_ai === 1);
      }
    } catch (error) {
      console.error("Error fetching settings", error);
    }
  };

  const getFilterParams = () => {
    const params = new URLSearchParams();
    if (filters.years.length > 0) params.append('years', filters.years.join(','));
    if (filters.months.length > 0) params.append('months', filters.months.join(','));
    if (filters.users.length > 0) params.append('users', filters.users.join(','));
    params.append('per_page', perPage);
    return params.toString();
  };

  const fetchLogs = async (page = 1) => {
    setLoadingLogs(true);
    try {
      const qs = getFilterParams();
      const response = await fetch(`${Apiurl}admin/ai-settings/logs?page=${page}&${qs}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.status) {
        setLogs(data.logs.data);
        setPagination({
          current_page: data.logs.current_page,
          last_page: data.logs.last_page,
          total: data.logs.total
        });
        setTotalTokens(data.total_tokens || 0);
      }
    } catch (error) {
      console.error("Error fetching logs", error);
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const qs = getFilterParams();
      const response = await fetch(`${Apiurl}admin/ai-settings/logs/export?${qs}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ai_prompt_logs_${new Date().getTime()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export error", error);
      Swal.fire('Error', 'Failed to export logs.', 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleToggle = async () => {
    const newState = !askAiEnabled;
    setAskAiEnabled(newState);
    setLoadingToggle(true);
    try {
      const response = await fetch(`${Apiurl}admin/ai-settings/toggle`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ ask_ai: newState ? 1 : 0 }),
      });
      const data = await response.json();
      if (!data.status) {
        setAskAiEnabled(!newState);
        Swal.fire('Error', 'Failed to update AI settings.', 'error');
      } else {
        Swal.fire({
          icon: 'success', title: 'Success', text: `Ask AI has been ${newState ? 'enabled' : 'disabled'}.`,
          timer: 1500, showConfirmButton: false
        });
      }
    } catch (error) {
      setAskAiEnabled(!newState);
      Swal.fire('Error', 'An error occurred while updating AI settings.', 'error');
    } finally {
      setLoadingToggle(false);
    }
  };

  const userOptionsMapped = useMemo(() => {
    if (!userOpt) return [];
    return userOpt.map(emp => ({ val: emp.id.toString(), label: `${emp.name} ${emp.last_name || ''}` }));
  }, [userOpt]);

  const toggleFilter = (type, val) => {
    setFilters(prev => {
        const current = prev[type];
        const exists = current.includes(val);
        return {
            ...prev,
            [type]: exists ? current.filter(item => item !== val) : [...current, val]
        };
    });
  };

  const toggleRowExpansion = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const cardStyle = {
    borderRadius: "14px", 
    border: "1px solid #cbd5e1", 
    boxShadow: "0 4px 24px -6px rgba(148, 163, 184, 0.12), 0 2px 8px -2px rgba(148, 163, 184, 0.06)"
  };

  return (
    <MainLayout title="AI Management">
      <div className="px-2 max-w-[1600px] mx-auto space-y-3 pb-8">

        {/* Top Controls: Inline Horizontal Flex Row */}
        <div className="flex flex-wrap items-center gap-3 w-full">
          
          {/* Filters Area (resembles FilterBar) */}
          <div className="flex items-center gap-2 flex-1">
             <FilterPill 
                name="Users" 
                id="users" 
                list={userOptionsMapped} 
                icon={<PersonIcon />} 
                selected={filters.users} 
                onToggle={toggleFilter}
                activeDropdown={activeDropdown}
                setActiveDropdown={setActiveDropdown}
             />
             <FilterPill 
                name="Year" 
                id="years" 
                list={yearOptions} 
                icon={<EventIcon />} 
                selected={filters.years} 
                onToggle={toggleFilter}
                activeDropdown={activeDropdown}
                setActiveDropdown={setActiveDropdown}
             />
             <MonthMultiSelectPicker 
                selectedMonths={filters.months}
                onToggle={toggleFilter}
                activeDropdown={activeDropdown}
                setActiveDropdown={setActiveDropdown}
             />
             
             {/* Export Button inline with filters */}
              <button 
                onClick={handleExport} disabled={exporting || logs.length === 0}
                className="flex items-center gap-1.5 h-8 px-3 rounded-xl text-[11px] font-bold border border-slate-200 text-slate-600 bg-white hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm ml-auto disabled:opacity-50"
                title="Export to Spreadsheet"
              >
                <Download className="w-3.5 h-3.5" />
                Export
              </button>
          </div>

          {/* Tokens Card */}
          <div className="bg-white p-1.5 rounded-[12px] shadow-sm flex items-center gap-2 shrink-0 border border-slate-200 pr-3">
                <div className="p-1.5 bg-blue-50 rounded-lg shrink-0">
                  <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div className="shrink-0 flex items-center gap-2">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest leading-none">Tokens Used</p>
                  <h3 className="text-sm font-bold text-slate-900 leading-none">{totalTokens.toLocaleString()}</h3>
                </div>
          </div>

          {/* Master Switch Card */}
          <div className="bg-white p-1.5 rounded-[12px] shadow-sm flex items-center gap-2 shrink-0 border border-slate-200 pr-3">
                <div className={`p-1.5 rounded-lg transition-colors duration-300 shrink-0 ${askAiEnabled ? 'bg-green-100' : 'bg-red-100'}`}>
                  <Power className={`w-4 h-4 ${askAiEnabled ? 'text-green-600' : 'text-red-600'}`} />
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest leading-none">AI Switch</p>
                  <button onClick={handleToggle} disabled={loadingToggle}
                    className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors duration-300 ${
                      askAiEnabled ? 'bg-green-500' : 'bg-slate-300'
                    } ${loadingToggle ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform duration-300 shadow-sm ${askAiEnabled ? 'translate-x-4' : 'translate-x-1'}`} />
                  </button>
                </div>
          </div>
        </div>

        {/* Logs Table Card */}
        <div className="bg-white flex flex-col transition-all duration-300" style={{ ...cardStyle, overflow: "hidden" }}>
          <div className="overflow-x-auto bg-white">
            <table className="w-full border-collapse text-left bg-white">
              <thead className="bg-[#04121f]">
                <tr>
                  <th className="px-4 py-3 text-sm font-bold text-white whitespace-nowrap w-16 border-r border-slate-700/50">S.No.</th>
                  <th className="px-4 py-3 text-sm font-bold text-white whitespace-nowrap w-40 border-r border-slate-700/50">Date & Time</th>
                  <th className="px-4 py-3 text-sm font-bold text-white whitespace-nowrap w-48 border-r border-slate-700/50">User</th>
                  <th className="px-4 py-3 text-sm font-bold text-white whitespace-nowrap min-w-[250px] border-r border-slate-700/50">Prompt Text</th>
                  <th className="px-4 py-3 text-sm font-bold text-white whitespace-nowrap min-w-[250px] border-r border-slate-700/50">AI Response</th>
                  <th className="px-4 py-3 text-sm font-bold text-white whitespace-nowrap w-32">Tokens</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {loadingLogs ? (
                  <tr><td colSpan="6" className="py-24 text-center bg-white"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-slate-400" /></td></tr>
                ) : logs.length === 0 ? (
                  <tr><td colSpan="6" className="py-16 text-center text-sm font-semibold text-slate-400 bg-white">No records available</td></tr>
                ) : (
                  logs.map((log, rowIndex) => {
                    const isExpanded = expandedRows[log.id];
                    const serialNumber = (pagination.current_page - 1) * perPage + rowIndex + 1;
                    return (
                      <tr key={log.id} className={`group hover:bg-slate-50/50 transition-colors align-top`}>
                        <td className="p-3 text-xs font-semibold text-slate-500">
                          {serialNumber}
                        </td>
                        <td className="p-3 text-xs font-mono font-medium text-slate-500">
                          {new Date(log.created_at).toLocaleString()}
                        </td>
                        <td className="p-3 text-sm text-slate-700 font-medium group-hover:text-slate-900">
                          <div className="font-bold text-slate-800 tracking-tight">{log.user?.name} {log.user?.last_name}</div>
                          <div className="text-[11px] text-slate-500 mt-0.5">{log.user?.email || 'N/A'}</div>
                        </td>
                        <td className="p-3 text-sm text-slate-700 font-medium group-hover:text-slate-900 max-w-xs">
                          <div className={`${!isExpanded ? 'line-clamp-3' : ''} whitespace-pre-wrap`}>
                            {log.prompt}
                          </div>
                          {log.prompt && log.prompt.length > 120 && (
                            <button onClick={() => toggleRowExpansion(log.id)} className="text-indigo-600 text-[11px] mt-1 hover:underline font-bold uppercase tracking-wider">
                              {isExpanded ? 'Show Less' : 'Read More'}
                            </button>
                          )}
                        </td>
                        <td className="p-3 text-sm text-slate-700 font-medium group-hover:text-slate-900 max-w-xs">
                          <div className={`${!isExpanded ? 'line-clamp-3' : ''} whitespace-pre-wrap`}>
                            {log.response || <span className="text-slate-400 italic">No response</span>}
                          </div>
                          {log.response && log.response.length > 120 && (
                            <button onClick={() => toggleRowExpansion(log.id)} className="text-indigo-600 text-[11px] mt-1 hover:underline font-bold uppercase tracking-wider">
                              {isExpanded ? 'Show Less' : 'Read More'}
                            </button>
                          )}
                        </td>
                        <td className="p-3 text-sm text-slate-700 font-medium group-hover:text-slate-900 whitespace-nowrap">
                          <div className="flex flex-col text-[11px] space-y-1">
                            <div><span className="font-bold text-slate-400">IN:</span> <span className="font-mono">{log.input_tokens || 0}</span></div>
                            <div><span className="font-bold text-slate-400">OUT:</span> <span className="font-mono">{log.output_tokens || 0}</span></div>
                            <div className="pt-1 mt-1 border-t border-slate-100 text-indigo-600"><span className="font-bold">TOT:</span> <span className="font-mono font-bold">{log.total_tokens || 0}</span></div>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loadingLogs && (
            <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                Showing Page {pagination.current_page} of {pagination.last_page}
              </span>
              <div className="flex items-center gap-4">
                {/* Rows Per Page */}
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-bold text-slate-500 uppercase">Show:</span>
                  <select 
                    className="border border-slate-200 rounded-md text-xs py-1 pl-2 pr-6 focus:ring-1 focus:ring-slate-500 focus:outline-none bg-white font-medium text-slate-700 cursor-pointer shadow-sm"
                    value={perPage}
                    onChange={(e) => { setPerPage(Number(e.target.value)); fetchLogs(1); }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={100000}>All</option>
                  </select>
                </div>
                
                <div className="flex gap-2">
                  <button 
                    disabled={pagination.current_page === 1}
                    onClick={() => fetchLogs(pagination.current_page - 1)}
                    className="px-4 py-1.5 text-xs font-bold uppercase tracking-wider border border-slate-200 bg-white text-slate-700 rounded-lg shadow-sm disabled:opacity-50 hover:bg-slate-50 transition-all"
                  >
                    Previous
                  </button>
                  <button 
                    disabled={pagination.current_page === pagination.last_page}
                    onClick={() => fetchLogs(pagination.current_page + 1)}
                    className="px-4 py-1.5 text-xs font-bold uppercase tracking-wider border border-slate-200 bg-white text-slate-700 rounded-lg shadow-sm disabled:opacity-50 hover:bg-slate-50 transition-all"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

      </div>
    </MainLayout>
  );
};

export default AdminAiManagement;
